import { Component, effect } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { FormValidationService } from '../../../core/services/form-validation.service';
import { ApiError } from '../../../core/models/common/api-error';
import { AuthService } from '../../../core/services/auth.service';
import { Store } from '@ngrx/store';
import { authActions } from '../../../core/store/auth';
import { Router } from '@angular/router';
import { ToastService } from '../../../core/services/toast.service';
import { Title } from '@angular/platform-browser';
import {
  isLoggedIn,
  selectAuthUser,
  selectAuthUserLoading,
} from '../../../core/store/reducers';
import { VendorsService } from '../../../core/services/vendors.service';
import { skipWhile, take } from 'rxjs';
import { Vendor } from '../../../core/models/entities/vendor';

@Component({
  selector: 'app-vendor-registration-page',
  template: `
    <app-page-section>
      <div class="grid grid-cols-1 gap-8 lg:grid-cols-3 xl:grid-cols-5">
        <div
          class="shadow-pricing col-span-1 -mx-4 rounded-lg bg-white p-6 md:-mx-0 md:p-8 md:py-16 lg:col-span-2 lg:px-20 xl:col-span-3"
        >
          <h1 class="md:text-center">Testez l'outil dès maintenant</h1>
          <div class="mt-6" id="signUp">
            Vous souhaitez proposer une ou plusieurs salles à louer en utilisant
            notre service de location de salle ? Vous pouvez dès aujourd’hui
            <app-text-highlight textSize="sm"
              ><span class="font-bold"
                >tester gratuitement</span
              ></app-text-highlight
            >
            notre système de création, gestion et location de salle en vous
            créant un compte.
          </div>
          <form class="mt-10" (submit)="submit()">
            @if (isLoggedIn() && formWhenLoggedIn) {
              <div class="mb-8">
                <app-alert variant="warning">
                  <i class="far fa-exclamation-triangle mr-2"></i>
                  <span class="font-bold"
                    >Vous êtes sur le point de créer un autre compte hôte.</span
                  ><br />
                  Le compte sera associé à votre compte utilisateur actuel ({{
                    authUser()?.email
                  }}).
                </app-alert>
              </div>
              <app-text-input
                [control]="formWhenLoggedIn.get('name')"
                name="name"
                label="Entreprise/Institution"
              ></app-text-input>
            } @else if (form) {
              <div class="grid md:grid-cols-2 md:gap-x-6">
                <app-text-input
                  [control]="form.get('first_name')"
                  name="first_name"
                  label="Prénom"
                ></app-text-input>
                <app-text-input
                  [control]="form.get('last_name')"
                  name="last_name"
                  label="Nom"
                ></app-text-input>
              </div>
              <app-text-input
                [control]="form.get('vendor_name')"
                name="vendor_name"
                label="Entreprise/Institution"
              ></app-text-input>
              <div class="grid md:grid-cols-2 md:gap-x-6">
                <app-text-input
                  [control]="form.get('email')"
                  name="email"
                  label="<i class='fa-regular fa-paper-plane'></i> Adresse e-mail"
                ></app-text-input>
                <app-password-input
                  [control]="form.get('password')"
                  name="password"
                  label="<i class='fa-regular fa-lock'></i> Mot de passe"
                ></app-password-input>
              </div>
            }

            <div class="mt-10 text-center">
              <app-button [loading]="sendingData" type="submit"
                >Je crée mon compte</app-button
              >
            </div>
          </form>
        </div>
        <div
          class="col-span-1 hidden overflow-visible lg:relative lg:block xl:col-span-2"
        >
          <img
            class="top-1/2 left-[-100px] z-[-1] mx-auto max-w-none -translate-y-1/2 md:absolute md:w-[500px] xl:w-[600px]"
            priority
            ngSrc="devenir-hote-header.png"
            width="1030"
            height="1090"
            sizes="80vw"
            alt="Devenir un hôte"
            title="Devenir un hôte"
          />
        </div>
      </div>
    </app-page-section>

    <app-page-section>
      <h2 class="mt-10 text-center">Comment ça marche ?</h2>

      <div class="mt-20 grid grid-cols-12 items-center">
        <!-- ---------------------------------------------------------------------------->
        <!-- Etape 1 -->
        <!-- ---------------------------------------------------------------------------->

        <div class="col-span-12 md:col-span-8 xl:col-span-6">
          <img
            src="/assets/img/1.svg"
            alt="location-salles.ch - Créez votre compte"
            title="location-salles.ch - Créez votre compte"
          />
          <div class="relative -top-12 left-0 lg:left-20 lg:pr-20">
            <h3>Créez votre compte</h3>
            <p class="mt-6"
              >Vous pouvez créer votre compte en moins d'une minute. Vous
              recevrez ensuite un e-mail dans votre boîte pour vérifier votre
              adresse e-mail.</p
            >
            <div class="mt-6">
              <app-button
                size="sm"
                variant="outlined"
                [routerLink]="['.']"
                fragment="signUp"
                >Je crée mon compte</app-button
              >
            </div>
          </div>
        </div>
        <div
          class="col-span-12 md:col-span-4 lg:justify-center lg:pr-16 xl:col-span-6"
        >
          <div
            class="relative hidden h-60 w-full translate-y-12 rounded-tr-lg border-t border-r border-gray-400 lg:flex"
          >
            <div class="absolute lg:-top-12 lg:right-1/3">
              <img
                class="w-24"
                ngSrc="coffee.png"
                width="95"
                height="92"
                alt="location-salles.ch - café"
                title="location-salles.ch - café"
              />
            </div>
          </div>
        </div>

        <!-- ---------------------------------------------------------------------------->
        <!-- Etape 2 -->
        <!-- ---------------------------------------------------------------------------->

        <div
          class="col-span-12 md:col-span-2 lg:col-span-3 lg:justify-center 2xl:col-span-5"
        >
          <div
            class="relative hidden h-60 w-1/2 translate-y-64 rounded-tl-lg border-t border-l border-gray-400 lg:ml-12 lg:flex xl:ml-24 2xl:w-3/4"
          >
            <div class="absolute top-20 -left-12">
              <img
                class="w-24"
                ngSrc="cles.png"
                width="96"
                height="89"
                alt="location-salles.ch - café"
                title="location-salles.ch - café"
              />
            </div>
          </div>
        </div>

        <div
          class="col-span-12 mt-16 flex justify-end md:col-span-10 lg:col-span-9 2xl:col-span-7"
        >
          <div>
            <div class="relative right-0 flex justify-end">
              <img
                src="/assets/img/2.svg"
                alt="location-salles.ch - Tester"
                title="location-salles.ch - Tester"
              />
            </div>
            <div class="relative -top-12 md:right-10 md:pl-10">
              <h3 class="text-right">
                Testez la console d'administration<br />et la réservation
                interne</h3
              >
              <p class="mt-6 xl:text-right"
                >Une fois votre compte créé, vous serez directement dirigé sur
                la console d'administration et vous pourrez gratuitement tester
                les fonctionnalités suivantes :</p
              >
              <div class="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
                <div
                  class="border-secondary-500 col-span-1 rounded-lg border p-6"
                >
                  <img
                    class="h-16"
                    src="/assets/img/inviter.svg"
                    alt="location-salles.ch - Tester"
                    title="location-salles.ch - Tester"
                  />
                  <div class="mt-6"
                    >Inviter vos collègues à utiliser la plateforme</div
                  >
                </div>
                <div
                  class="border-secondary-500 col-span-1 rounded-lg border p-6"
                >
                  <img
                    class="h-16"
                    src="/assets/img/creer-salles.svg"
                    alt="location-salles.ch - Tester"
                    title="location-salles.ch - Tester"
                  />
                  <div class="mt-6"
                    >Créer vos salles avec toutes leurs spécificités</div
                  >
                </div>
                <div
                  class="border-secondary-500 col-span-1 rounded-lg border p-6"
                >
                  <img
                    class="h-16"
                    src="/assets/img/reservation-interne.svg"
                    alt="location-salles.ch - Tester"
                    title="location-salles.ch - Tester"
                  />
                  <div class="mt-6">Créer vos 1res réservations internes</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ---------------------------------------------------------------------------->
        <!-- Etape 3 -->
        <!-- ---------------------------------------------------------------------------->

        <div class="col-span-12 mt-16 md:col-span-10 lg:col-span-8 lg:mt-32">
          <img
            src="/assets/img/3.svg"
            alt="location-salles.ch - Louez vos salles à l'externe"
            title="location-salles.ch - Louez vos salles à l'externe"
          />
          <div class="relative -top-12 left-0 lg:left-20 lg:pr-20">
            <h3>Louez vos salles à l'externe</h3>
            <p class="mt-6 text-pretty"
              >Notre solution devient payante lorsque vous souhaitez louer vos
              salles à l'externe. Cette solution inclut donc votre page publique
              où vos clients pourront rechercher une salle parmi celles que vous
              avez créées pour l'externe, consulter les disponibilités et vous
              faire parvenir une demande de location après avoir payé en
              ligne.</p
            >
            <p class="mt-6 text-pretty"
              >Le processus peut se faire directement depuis la console
              d'administration sous le menu Organisation > Paiement en ligne.</p
            >
            <div
              class="mt-8 flex flex-col space-y-4 lg:flex-row lg:space-y-0 lg:space-x-8"
            >
              <div>
                <a class="no-underline" href="mailto:contact@ercos.ch">
                  <i class="fal fa-paper-plane mr-1"></i>
                  contact&#64;ercos.ch
                </a>
              </div>
              <div>
                <a class="no-underline" href="tel:0215253060">
                  <i class="fal fa-phone mr-1"></i>
                  021 525 30 60
                </a>
              </div>
              <div>
                <a
                  class="no-underline"
                  href="https://www.ercos.ch"
                  target="_blank"
                >
                  <i class="fal fa-globe mr-1"></i>
                  ercos.ch
                </a>
              </div>
            </div>
          </div>
        </div>

        <div
          class="col-span-12 md:col-span-2 lg:col-span-4 lg:justify-center lg:pr-16"
        >
          <div
            class="relative hidden h-72 w-full translate-y-32 rounded-tr-lg border-t border-r border-gray-400 lg:flex"
          >
            <div class="absolute top-16 -right-16">
              <img
                class="w-32"
                ngSrc="cactus.png"
                width="128"
                height="128"
                alt="location-salles.ch - cactus"
                title="location-salles.ch - cactus"
              />
            </div>
          </div>
        </div>

        <!-- ---------------------------------------------------------------------------->
        <!-- Etape 4 -->
        <!-- ---------------------------------------------------------------------------->

        <div
          class="col-span-12 hidden md:col-span-4 md:flex lg:col-span-6"
        ></div>

        <div
          class="col-span-12 mt-10 flex justify-end md:col-span-8 lg:col-span-6 lg:mt-16"
        >
          <div>
            <div class="relative right-0 flex justify-end">
              <img
                src="/assets/img/4.svg"
                alt="location-salles.ch - Accompagnement"
                title="location-salles.ch - Accompagnement"
              />
            </div>
            <div class="relative -top-12 lg:right-10 lg:pl-10">
              <h3 class="text-right"
                >Nous vous accompagnons<br />dans cette transition</h3
              >
              <p class="mt-6 xl:text-right"
                >Nous comprenons qu'un changement d'outil peut faire peur à une
                équipe dont les habitudes sont bien installées. C'est pourquoi
                nous ne vous laissons pas seul, et nous prévoyons de venir dans
                vos locaux afin de former vos collaborateurs et faire en sorte
                qu'ils se familiarisent avec l'outil.</p
              >
            </div>
          </div>
        </div>
      </div>

      <div class="mt-6 flex justify-center">
        <app-button [routerLink]="['./']" fragment="signUp"
          >Créer un compte</app-button
        >
      </div>
    </app-page-section>

    <app-page-section>
      <div class="grid grid-cols-1 items-center md:grid-cols-2">
        <div class="col-span-1">
          <img
            class="mx-auto xl:w-3/4"
            ngSrc="contact-direct.png"
            width="1412"
            height="1352"
            sizes="(max-width: 768px) 80vw, 25vw"
            alt="Nous contacter"
            title="Nous contacter"
          />
        </div>
        <div class="col-span-1 mt-12 md:mt-0">
          <div class="relative -top-6 -left-8 flex space-x-2">
            <div class="bg-secondary-500 mt-2.5 h-px w-16"></div>
            <i class="fal fa-phone"></i>
          </div>
          <h2>Vous préférez nous contacter directement ?</h2>
          <p class="mt-6">Nous nous ferons un plaisir de vous renseigner !</p>
          <div
            class="mt-8 flex flex-col space-y-4 lg:flex-row lg:space-y-0 lg:space-x-8"
          >
            <div>
              <a class="no-underline" href="mailto:contact@ercos.ch">
                <i class="fal fa-paper-plane mr-1"></i>
                contact&#64;ercos.ch
              </a>
            </div>
            <div>
              <a class="no-underline" href="tel:0215253060">
                <i class="fal fa-phone mr-1"></i>
                021 525 30 60
              </a>
            </div>
            <div>
              <a
                class="no-underline"
                href="https://www.ercos.ch"
                target="_blank"
              >
                <i class="fal fa-globe mr-1"></i>
                ercos.ch
              </a>
            </div>
          </div>
        </div>
      </div>
    </app-page-section>
  `,
  standalone: false,
})
export class VendorRegistrationPageComponent {
  form: FormGroup;
  formWhenLoggedIn: FormGroup;
  sendingData = false;
  isLoggedIn = this.store.selectSignal(isLoggedIn);
  authUser = this.store.selectSignal(selectAuthUser);

  constructor(
    private formValidationService: FormValidationService,
    private authService: AuthService,
    private vendorsService: VendorsService,
    private store: Store,
    private router: Router,
    private toastService: ToastService,
    private title: Title,
  ) {
    this.title.setTitle('Location de salles - Créer un compte');

    effect(() => {
      if (this.isLoggedIn()) {
        this.setupFormWhenLoggedIn();
      } else {
        this.setupFormWhenGuest();
      }
    });
  }

  setupFormWhenGuest() {
    this.form = new FormGroup({
      first_name: new FormControl('', [Validators.required]),
      last_name: new FormControl('', [Validators.required]),
      vendor_name: new FormControl('', [Validators.required]),
      email: new FormControl('', [Validators.required, Validators.email]),
      password: new FormControl('', [Validators.required]),
    });
  }

  setupFormWhenLoggedIn() {
    this.formWhenLoggedIn = new FormGroup({
      name: new FormControl('', [Validators.required]),
    });
  }

  async submit() {
    let form: FormGroup;
    if (this.isLoggedIn()) {
      form = this.formWhenLoggedIn;
    } else {
      form = this.form;
    }
    if (this.formValidationService.validate(form)) {
      try {
        this.sendingData = true;
        if (this.isLoggedIn()) {
          const vendorCreated: Vendor = await this.vendorsService.store(
            form.value,
          );
          this.store.dispatch(authActions.findCurrentUser());
          this.store
            .select(selectAuthUserLoading)
            .pipe(
              skipWhile((loading) => loading),
              take(1),
            )
            .subscribe(() => {
              this.store.dispatch(
                authActions.setVendor({
                  vendor: this.store
                    .selectSignal(selectAuthUser)()
                    .vendors.find((el) => el.id === vendorCreated.id),
                }),
              );
              this.redirectAfterLogin();
            });
        } else {
          const response = await this.authService.registerVendor(form.value);
          this.store.dispatch(authActions.loginSuccess(response));
          await this.redirectAfterLogin();
        }
      } catch (exception) {
        console.error(exception);
        this.formValidationService.displayApiValidationErrors(
          this.form,
          exception as ApiError,
        );
      }
    }
  }

  private async redirectAfterLogin() {
    await this.router.navigate(['/salles']);
    this.toastService.success('Le compte hôte a été créé avec succès.');
    this.sendingData = false;
  }
}
